import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api/index.js';
import type { components } from '@/lib/api/schema.gen.js';

export type FaceEnrolmentStatus = components['schemas']['FaceEnrolmentStatus'];
export type FaceEnrolment = components['schemas']['FaceEnrolment'];

/**
 * The request body, taken from the generated contract rather than written
 * out here.
 *
 * `api.post` takes `unknown`, so a body of the wrong shape compiles
 * perfectly and fails at runtime with a 400 — which is exactly what would
 * have happened when the server moved from a flat frame list to a
 * per-angle one, with nothing in this repo complaining. Naming the contract
 * type makes that a build error instead.
 */
export type EnrolFaceBody = components['schemas']['EnrolFace'];
export type FacePose = EnrolFaceBody['captures'][number]['pose'];

const KEY = ['attendance', 'face'] as const;

/**
 * Whether you have set up face check-in.
 *
 * Three states, not two: `available` is false when no face service is
 * configured, which is a different thing from being enrolled or not — the
 * card shows nothing at all rather than offering a button that would refuse.
 */
export function useFaceEnrolment() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<FaceEnrolmentStatus>('/attendance/face'),
  });
}

/**
 * Sends a completed guided scan.
 *
 * The frames are base64 JPEG without the data-URL prefix, grouped by the
 * angle they were taken at. They are never held anywhere on this side
 * either — they exist as a local array for the length of one request.
 *
 * **There is no hook to delete an enrolment**, and the route is gone too.
 * Face check-in is required in order to check in, so deleting your own
 * template is deleting your own ability to record a day. Running the scan
 * again replaces it, which is what somebody actually wants when their face
 * has changed. HR can clear a bad enrolment, and leaving the company erases
 * it.
 */
export function useEnrolFace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: EnrolFaceBody) => api.post<FaceEnrolmentStatus>('/attendance/face', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/* ------------------------------------------------------------------ *
 * Asking to redo a scan
 * ------------------------------------------------------------------ */

export type FaceUpdateRequest = components['schemas']['FaceUpdateRequest'];

const APPROVER_KEY = ['attendance', 'face', 'update-requests'] as const;

/**
 * Asks the reporting manager to unlock a rescan.
 *
 * The first scan is somebody's own to run; every one after it goes through
 * the person they report to, because a template its owner can replace
 * silently proves nothing about who stood at the camera.
 *
 * Invalidates the status, which carries `mayScan` — so the card stops
 * offering the scan and starts showing the request it just made.
 */
export function useRequestFaceUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { reason: string }) =>
      api.post<FaceUpdateRequest>('/attendance/face/update-request', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Takes back a question nobody has answered yet. */
export function useCancelFaceUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<FaceUpdateRequest>(`/attendance/face/update-request/${id}/cancel`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** The rescans waiting on this approver. Their tree, or the company for HR. */
export function useFaceUpdateRequests(enabled = true) {
  return useQuery({
    queryKey: APPROVER_KEY,
    queryFn: () =>
      api.get<{ items: FaceUpdateRequest[] }>('/attendance/face/update-requests', {
        query: { status: 'PENDING' },
      }),
    enabled,
  });
}

export function useDecideFaceUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; approve: boolean; remarks?: string }) =>
      api.post<FaceUpdateRequest>(`/attendance/face/update-requests/${input.id}/decide`, {
        approve: input.approve,
        remarks: input.remarks || null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attendance', 'face'] }),
  });
}

/* ------------------------------------------------------------------ *
 * Proving a live face, at check-in
 * ------------------------------------------------------------------ */

export type FaceChallenge = components['schemas']['FaceChallenge'];
export type VerifyFaceBody = components['schemas']['VerifyFace'];

/**
 * Asks for a challenge, answers it, and hands back the id to spend.
 *
 * One function rather than two hooks, because the two halves are useless
 * apart: a challenge nobody answers is litter, and an answer with no
 * challenge is refused. The punch then spends the id.
 *
 * The frames never leave this call. They are built from the video, posted,
 * and dropped.
 */
/**
 * How many bursts to try before giving up and saying so.
 *
 * **A phone does not tell you it failed; it keeps looking.** Face unlock on
 * an Android handset has no better camera than a laptop and no special
 * hardware — what it has is a loop. It watches until it sees what it needs
 * and succeeds the moment it does, so a blink that fell between two frames
 * costs a fraction of a second rather than an error message and a second
 * attempt by hand.
 *
 * This is the cheap half of that. It cannot watch continuously — the model
 * is on the server — but it can stop making somebody press the button again
 * for a sampling problem that is ours.
 */
const ATTEMPTS = 3;

/** Failures worth trying again for: the ones another burst might fix. */
const WORTH_RETRYING = new Set(['LIVENESS_FAILED', 'NO_FACE', 'POOR_QUALITY', 'MANY_FACES']);

export async function proveFace(
  capture: (count: number) => Promise<string[]>,
  onRetry?: () => void,
): Promise<string> {
  let lastError: unknown;

  for (let attempt = 0; attempt < ATTEMPTS; attempt += 1) {
    // A challenge each time. They are single-use by design, and reusing a
    // spent one is refused — correctly.
    const challenge = await api.post<FaceChallenge>('/attendance/face/challenge', {});

    /**
     * Enough frames, close enough together, to actually catch a blink.
     *
     * A blink's closed phase is about a tenth of a second. Twelve frames
     * 120 ms apart covered 1.4 seconds but sampled it coarsely enough that
     * the closed phase could fall cleanly between two of them — a blink the
     * person definitely made, and the server never saw.
     *
     * Twenty frames 50 ms apart is a **shorter** window, not a longer one:
     * one second, sampled finely enough that a blink lands in two or three
     * frames. Catching it is about the sampling rate, not standing there
     * longer.
     */
    const frames = await capture(20);

    try {
      await api.post<{ challengeId: string }>('/attendance/face/verify', {
        challengeId: challenge.challengeId,
        frames,
      } satisfies VerifyFaceBody);

      return challenge.challengeId;
    } catch (error) {
      lastError = error;

      // "That is not you" will not become "that is you" on a second look,
      // and neither will a template that needs redoing. Trying again for
      // those wastes somebody's time and tells an impostor how many goes
      // they get.
      if (!WORTH_RETRYING.has(codeOf(error)) || attempt === ATTEMPTS - 1) throw error;

      onRetry?.();
    }
  }

  throw lastError;
}

/** The server's machine-readable reason, where it gave one. */
const codeOf = (error: unknown): string => (ApiError.is(error) ? (error.failure ?? '') : '');

/** What the server will ask somebody to do, so the card can say it first. */
/**
 * Four words, in the imperative.
 *
 * It is read over a moving picture of somebody's own face, in about a
 * second, by somebody whose hand is on a button. "Look at the camera and
 * blink" is two instructions; the camera is already in front of them.
 */
export const FACE_PROMPT = 'Blink once';
